import {
	differenceInCalendarDays,
	format,
	type Locale,
	parse,
	startOfDay,
} from "date-fns";
import { cs } from "date-fns/locale/cs";
import { enUS } from "date-fns/locale/en-US";
import { parseTimelineDate } from "web_myplann/NotificationCenter/Utils/parseTimelineDate";
import { DEFAULT_DATE_FORMAT } from "web_myplann/Shared/Utils/dateFormatters";

export const formatTimelineDate = (isoDate: string): string =>
	format(parseTimelineDate(isoDate), DEFAULT_DATE_FORMAT, { locale: cs });

export type RelativeDateLabels = {
	today: string;
	yesterday: string;
	daysAgo: (count: number) => string;
};

export const formatRelativeTimelineDate = (
	isoDate: string,
	today: Date,
	labels: RelativeDateLabels,
): string => {
	const diff = differenceInCalendarDays(
		startOfDay(today),
		parseTimelineDate(isoDate),
	);

	if (diff === 0) return labels.today;
	if (diff === 1) return labels.yesterday;
	if (diff > 1) return labels.daysAgo(diff);
	return formatTimelineDate(isoDate);
};

const TIME_FORMATS: ReadonlyArray<{ format: string; locale: Locale }> = [
	{ format: "HH:mm:ss", locale: cs },
	{ format: "HH:mm", locale: cs },
	{ format: "h:mm:ss a", locale: enUS },
	{ format: "h:mm a", locale: enUS },
];

// Fixed non-DST reference date: date-fns `parse` writes H/M onto the
// reference's Y/M/D, and a same-day reference can flip across DST.
const TIME_PARSE_REFERENCE = new Date(2000, 0, 1);

const parseTimeOfDay = (time: string): Date | null => {
	const trimmed = time.trim();
	for (const { format: fmt, locale } of TIME_FORMATS) {
		const parsed = parse(trimmed, fmt, TIME_PARSE_REFERENCE, { locale });
		if (!Number.isNaN(parsed.getTime())) {
			return parsed;
		}
	}
	return null;
};

export const formatTimelineTime = (time: string): string => {
	const parsed = parseTimeOfDay(time);
	return parsed ? format(parsed, "HH:mm") : time.trim();
};

/**
 * "09:00 - 10:00" for an activity with an end time, plain "09:00" otherwise.
 * An end time equal to the start is collapsed, so a zero-length slot does not
 * read as a range.
 */
export const formatTimelineTimeRange = (
	time: string,
	timeTo?: string | null,
): string => {
	const from = formatTimelineTime(time);
	if (!timeTo || timeTo.trim().length === 0) return from;
	const to = formatTimelineTime(timeTo);
	return to === from ? from : `${from} - ${to}`;
};
